import type { ChangeEvent } from "react";
import { useCallback, useMemo, useState } from "react";
import { Box, Input as MuiInput } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { ReactComponent as SearchIcon } from "../../assets/svg/Search.svg";
import { LabelBox, Button } from "../../components";
import { colors } from "../../theme/colors";
import { useGetOrgansQuery, useDeleteOrganMutation } from "../../services/api";
import { CustomTable } from "../../components/DataTable";
import style from "./organ.module.css";
import { CustomActionMenu } from "../../components/Menu";
import type { ActionItem } from "../../components/Menu";
import { ReactComponent as EditIcon } from "../../assets/svg/edit.svg";
import { ConfirmationDialog } from "../../components/Dialog";
import { getErrorMessage } from "../../utils/common";

type OrganAction = "edit" | "delete";

/**
 * Organs listing page with search, edit and delete row actions.
 *
 * Notes:
 * - Organs are hard-deleted rather than deactivated, so there is no status
 *   column, no reason prompt and no logs dialog — unlike the other
 *   reference-data screens, which soft-deactivate.
 */
const Organs = () => {
  const [deleteOrgan] = useDeleteOrganMutation();
  const navigate = useNavigate();

  // all query parameters and pagination params
  const [queryParam] = useState({
    order_by_columns: "name",
    order: "Asc",
    is_detailed: true,
  });
  const { data: organData, isFetching } = useGetOrgansQuery(queryParam);

  // The organ endpoint takes no search parameter, so filtering happens here
  // over the fetched rows rather than as part of the query.
  const [searchTerm, setSearchTerm] = useState("");

  const organs: Organ[] = useMemo(() => {
    const items: Organ[] = organData?.items ?? [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return items;
    return items.filter((organ) => organ.name?.toLowerCase().includes(term));
  }, [organData, searchTerm]);

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedOrgan, setSelectedOrgan] = useState<Organ | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handles action menu selections for an organ row.
   *
   * Args:
   *   value (OrganAction): Action selected from the row menu.
   *   item (Organ): Organ associated with the action.
   *
   * Returns:
   *   void
   */
  const handleMenu = useCallback(
    (value: OrganAction, item: Organ) => {
      setSelectedOrgan(item);
      switch (value) {
        case "edit":
          // Hand the row over so the edit screen does not have to re-fetch it.
          navigate(`/organs/edit/${item?.id}`, { state: { organ: item } });
          break;
        case "delete":
          setModalVisible(true);
          break;
      }
    },
    [navigate]
  );

  /**
   * Column definitions for the Organs table.
   *
   * Returns:
   *   Array<object>: Column configuration for CustomTable.
   */
  const fields = useMemo(
    () => [
      { id: "name", label: "Organ Name", flex: 2, sortable: true },
      {
        id: "actions",
        label: "Actions",
        type: "action",
        render: (item: Organ) => (
          <CustomActionMenu<Organ, OrganAction>
            item={item}
            isActive={item.is_active ?? false}
            handleChange={handleMenu}
            actions={
              [
                {
                  label: "Edit",
                  value: "edit",
                  icon: <EditIcon height={24} width={24} />,
                },
                {
                  label: "Delete",
                  value: "delete",
                  icon: <DeleteOutlineIcon height={24} width={24} />,
                },
              ] as ActionItem<OrganAction>[]
            }
          />
        ),
      },
    ],
    [handleMenu]
  );

  /**
   * Updates the search term in query params.
   *
   * Args:
   *   e (ChangeEvent<HTMLInputElement>): Search input change event.
   *
   * Returns:
   *   void
   */
  const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  /**
   * Deletes the selected organ.
   *
   * Returns:
   *   Promise<void>
   *
   * Notes:
   * - Deletion is permanent; the confirmation dialog is the only safeguard.
   */
  const handleDelete = async () => {
    try {
      // Guard against submitting without a selected row; avoids invalid deletes.
      if (!selectedOrgan) {
        setMessage({
          type: "error",
          text: "Please select an organ before deleting.",
        });
        return;
      }
      setIsLoading(true);
      await deleteOrgan(selectedOrgan.id).unwrap();
      setIsLoading(false);
      setModalVisible(false);
      setSelectedOrgan(null);
    } catch (error: unknown) {
      setIsLoading(false);
      setModalVisible(false);
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Something went wrong!"),
      });
    }
  };

  // Render the Organ component
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "15px",
        }}
      >
        <MuiInput
          disableUnderline
          placeholder="Search by name...."
          type="search"
          startAdornment={<SearchIcon width={20} height={20} />}
          value={searchTerm}
          onChange={handleSearch}
          sx={{ width: "550px", gap: "8px", borderRadius: "50px", backgroundColor: colors.white }}
        />
        <Link to="add">
          <Button
            variant="contained"
            className={style.addButton}
            label="Add New Organ"
            sx={{
              ":hover": { backgroundColor: colors.primary },
            }}
          />
        </Link>
      </Box>
      {message && (
        <Box mt={2}>
          <LabelBox
            boxProps={{ height: 50, display: "flex", alignItems: "center" }}
            data={message}
            onClose={() => setMessage(null)}
          />
        </Box>
      )}
      <Box style={{ maxHeight: "calc(100vh - 195px)", overflow: "auto" }}>
        <CustomTable<Organ>
          fields={fields}
          paginationRequired={true}
          data={organs}
          loading={isFetching}
          onDoubleClick={(organ: Organ) => {
            navigate(`/organs/edit/${organ?.id}`, { state: { organ } });
          }}
          defaultOrderBy="name"
          searchTerm={searchTerm}
          onClearSearch={() => setSearchTerm("")}
        />
      </Box>
      {modalVisible && (
        <ConfirmationDialog
          visible={modalVisible}
          message={`Are you sure you want to delete "${selectedOrgan?.name}"? This cannot be undone.`}
          loading={isLoading}
          onClose={() => {
            setModalVisible(false);
          }}
          onConfirm={handleDelete}
        />
      )}
    </Box>
  );
}
export default Organs;
